import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, timeout, retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DocumentHandlerService {
  private readonly DEFAULT_TIMEOUT = 10000; // 10 seconds
  private readonly MAX_RETRY_ATTEMPTS = 2;

  constructor(private http: HttpClient) {}

  downloadFile(url: string, filename?: string): Observable<void> {
    if (!url) {
      return throwError('Invalid URL provided');
    }

    const token = this.getAuthToken();
    if (!token) {
      return throwError('Authentication token not available');
    }

    const headers = this.createAuthHeaders(token);

    return this.http
      .get(url, {
        headers: headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        retry(this.MAX_RETRY_ATTEMPTS),
        map((response: HttpResponse<Blob>) => {
          if (response.body && response.body.size > 0) {
            this.triggerDownload(response.body, filename || this.extractFilenameFromUrl(url) || 'document');
            return;
          }
          throw new Error('No file data received');
        }),
        catchError((error) => this.handleError(error, 'download'))
      );
  }

  downloadDocument(url: string, token: string, filename?: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .get(url, {
        headers: headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        retry(this.MAX_RETRY_ATTEMPTS),
        map((response: HttpResponse<Blob>) => {
          if (response.body && response.body.size > 0) {
            this.triggerDownload(response.body, filename || this.extractFilenameFromUrl(url) || 'document');
            return { success: true, message: 'Download started successfully' };
          }
          return { success: false, message: 'No file data received' };
        }),
        catchError((error) => {
          const errorMessage = this.getErrorMessage(error);
          console.error('Download failed:', error);
          return throwError({ success: false, message: errorMessage });
        })
      );
  }

  getSecureBlobUrl(url: string): Observable<string> {
    if (!url) {
      return throwError('Invalid URL provided');
    }

    const token = this.getAuthToken();
    if (!token) {
      return throwError('Authentication token not available');
    }

    const headers = this.createAuthHeaders(token);

    return this.http
      .get(url, {
        headers: headers,
        responseType: 'blob',
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        retry(this.MAX_RETRY_ATTEMPTS),
        map((blob: Blob) => {
          if (!blob || blob.size === 0) {
            throw new Error('No data received');
          }
          return window.URL.createObjectURL(blob);
        }),
        catchError((error) => this.handleError(error, 'load secure URL'))
      );
  }

  getSecureImageUrl(url: string, token: string): Observable<string> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .get(url, {
        headers: headers,
        responseType: 'blob',
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        retry(this.MAX_RETRY_ATTEMPTS),
        map((blob: Blob) => {
          if (!blob || blob.size === 0) {
            throw new Error('No image data received');
          }
          return window.URL.createObjectURL(blob);
        }),
        catchError((error) => {
          const errorMessage = this.getErrorMessage(error);
          console.error('Failed to load image:', error);
          return throwError(errorMessage);
        })
      );
  }

  viewDocument(url: string, token: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .get(url, {
        headers: headers,
        responseType: 'blob',
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        retry(this.MAX_RETRY_ATTEMPTS),
        map((blob: Blob) => {
          if (!blob || blob.size === 0) {
            throw new Error('No document data received');
          }

          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');

          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 30000);

          return { success: true, message: 'Document opened successfully' };
        }),
        catchError((error) => {
          const errorMessage = this.getErrorMessage(error);
          console.error('Failed to view document:', error);
          return throwError({ success: false, message: errorMessage });
        })
      );
  }

  private createAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });
  }

  private getAuthToken(): string {
    const possibleTokens = [
      localStorage.getItem('authToken'),
      localStorage.getItem('token'),
      localStorage.getItem('_act_ls'),
      localStorage.getItem('access_token'),
      sessionStorage.getItem('authToken'),
      sessionStorage.getItem('token'),
      sessionStorage.getItem('_act_ls'),
      sessionStorage.getItem('access_token'),
    ];

    let token = possibleTokens.find((t) => t !== null) || '';

    if (token) {
      try {
        if (token.startsWith('{')) {
          const parsed = JSON.parse(token);
          token = parsed.access_token || parsed.token || parsed.authToken || parsed.accessToken || token;
        }
      } catch (e) {}
    }

    return token;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL after a short delay
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  }

  private handleError(error: any, operation: string): Observable<never> {
    const errorMessage = this.getErrorMessage(error);
    console.error(`Failed to ${operation}:`, error);
    return throwError(errorMessage);
  }

  private getErrorMessage(error: any): string {
    if (error.name === 'TimeoutError') {
      return 'Request timed out. Please check your connection and try again.';
    }

    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 0:
          return 'Unable to connect to server. Please check your internet connection.';
        case 401:
          return 'Authentication failed. Please log in again.';
        case 403:
          return 'You do not have permission to access this resource.';
        case 404:
          return 'The requested document was not found.';
        case 408:
          return 'Request timed out. Please try again.';
        case 500:
        case 502:
        case 503:
        case 504:
          return 'Server error. Please try again later.';
        default:
          return error.error?.message || `Server returned error ${error.status}. Please try again.`;
      }
    }

    if (error.message) {
      return error.message;
    }

    return 'An unexpected error occurred. Please try again.';
  }

  private extractFilenameFromUrl(url: string): string {
    if (!url) return 'document';

    try {
      const cleanUrl = url.split('?')[0].split('#')[0];
      const parts = cleanUrl.split('/');
      const filename = parts[parts.length - 1];

      if (filename && filename.includes('.')) {
        return filename;
      }

      const isImage =
        cleanUrl.toLowerCase().includes('photo') ||
        cleanUrl.toLowerCase().includes('image') ||
        cleanUrl.toLowerCase().includes('.jpg') ||
        cleanUrl.toLowerCase().includes('.png') ||
        cleanUrl.toLowerCase().includes('.jpeg');

      return isImage ? 'document.jpg' : 'document.pdf';
    } catch (error) {
      console.warn('Error extracting filename from URL:', error);
      return 'document.pdf';
    }
  }
}
